<template>
  <div class="payments-container">
    <SecurePaymentMethods class="payment-methods" title="Ways to pay securely" />
    <section class="secure-payment-container">
      <h2>Payment security</h2>
      <ul class="secure-payment-images security-images">
        <li v-for="method in securityMethods" :key="method.name" class="security-badge">
          <img :src="method.logo" :alt="method.name" />
        </li>
      </ul>
    </section>
    <p class="secure-payment-text">
      Encryption and payment security measures help protect card information during processing.
    </p>
  </div>
</template>

<script setup lang="ts">
import SecurePaymentMethods from '@/templates/baseline/components/PaymentInfo/SecurePaymentMethods.vue'

import mastercardIdCheckLogo from '../assets/payment-security/mastercard-id-check.svg'
import pciDssLogo from '../assets/payment-security/pci-dss.svg'
import sslLogo from '../assets/payment-security/ssl-secure.svg'
import threeDSecureLogo from '../assets/payment-security/three-d-secure.svg'
import visaSecureLogo from '../assets/payment-security/visa-secure.svg'

const securityMethods = [
  { name: 'PCI DSS Compliant', logo: pciDssLogo },
  { name: '3D Secure', logo: threeDSecureLogo },
  { name: 'SSL Encrypted', logo: sslLogo },
  { name: 'Visa Secure', logo: visaSecureLogo },
  { name: 'Mastercard ID Check', logo: mastercardIdCheckLogo },
]
</script>

<style scoped lang="scss">
.payments-container {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  max-width: 1120px;
  margin: 0 auto;
  gap: var(--sizes-6) var(--sizes-12);
}

:deep(.secure-payment-container) {
  display: grid;
  align-content: start;
  align-items: start;
  justify-items: center;
  min-width: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  gap: var(--sizes-4);

  h2 {
    display: grid;
    align-content: center;
    min-height: calc(var(--font-line-height-m) * 2);
    text-align: center;
    font-family: var(--font-family-heading);
    font-size: var(--font-size-m);
    font-weight: var(--font-weight-medium);
    line-height: var(--font-line-height-m);
  }
}

:deep(.secure-payment-images) {
  align-items: center;
  justify-content: center;
  min-height: 72px;
  margin: 0;
  gap: var(--sizes-3);
}

:deep(.payment-methods img) {
  width: var(--sizes-12);
  height: var(--sizes-8);
  object-fit: contain;
}

.security-images {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  width: min(100%, 368px);
  padding: 0;
  list-style: none;
}

.security-badge {
  position: relative;
  min-width: 0;
  min-height: 0;
  padding: 0;
  border-radius: var(--r-radius-full);
  background: var(--background-brand-subtle);
  aspect-ratio: 1;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    padding: var(--sizes-3);
    object-fit: contain;
  }
}

.secure-payment-text {
  grid-column: 1 / -1;
  max-width: 640px;
  margin: 0 auto;
  text-align: center;
  color: var(--content-secondary);
  font-size: var(--font-size-2xs);
  line-height: var(--font-line-height-xs);
}

@media (max-width: 760px) {
  .payments-container {
    grid-template-columns: 1fr;
    gap: var(--sizes-6);
  }
}
</style>
